import { useMemo, useRef } from 'react';
import { View } from 'react-native';
import { DEVICE_SIZES, useDeviceSize } from 'rn-responsive-styles';

import { isDeviceMinimumSize, DefaultLayout, FlashList } from '@pletly/ui';
import { useRefreshControl } from './useRefreshControl';
import { useScrollEndReached } from './useScrollEndReached';
import { styles } from './styles';
import { Props, ScrollListenerType } from './types';

export const ListDefaultLayout = <ItemT,>({
	style,
	bottomGutter,
	onRefresh,
	columnRight,
	onEndReached,
	inverted,
	scrollListenerType = 'list',
	...listProps
}: Props<ItemT>) => {
	const listRef = useRef<View>(null);
	const { refreshControl } = useRefreshControl(onRefresh);
	const deviceSize = useDeviceSize();

	useScrollEndReached({
		containerRef: getScrollContainer(scrollListenerType, listRef.current),
		inverted: inverted,
		onEndReached,
	});

	const columnRightNode = useMemo(() => {
		if (!isDeviceMinimumSize(deviceSize, DEVICE_SIZES.LG) || !columnRight) {
			return null;
		}

		return <View style={styles.columnRight}>{columnRight}</View>;
	}, [columnRight, deviceSize]);

	return (
		//@ts-expect-error flex: null works for web
		<DefaultLayout style={[style, flexReset]} bottomGutter={bottomGutter} ref={listRef}>
			<FlashList
				contentContainerStyle={{
					...styles.scrollContainer,
					...(bottomGutter ? styles.bottomGutter : {}),
				}}
				refreshControl={refreshControl}
				scrollIndicatorInsets={{ right: 1 }}
				inverted={inverted}
				{...listProps}
				onEndReached={undefined}
				style={styles.listStyle}
			/>
			{columnRightNode}
		</DefaultLayout>
	);
};

const flexReset = { flex: null };

const getScrollContainer = (
	scrollListenerType: ScrollListenerType,
	listRef?: React.RefObject<View>['current'],
) => {
	if (scrollListenerType === 'container') {
		return getCardContainer();
	}

	if (scrollListenerType === 'list' && listRef) {
		return listRef as unknown as HTMLDivElement;
	}

	return undefined;
};

const getCardContainer = () => {
	// This ID is added to @react-navigation/stack Card component by patch
	const ID = 'stack-card';

	return document.getElementById(ID) || undefined;
};
